import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { UserCircleIcon, KeyIcon, EnvelopeIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import ChangePasswordForm from '../components/auth/ChangePasswordForm';

const ProfilePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handlePasswordChanged = async () => {
    // Logout after password change
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t('nav.profile')}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Manage your account settings and change your password
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Info Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <UserCircleIcon className="h-5 w-5 mr-2 text-primary-600" />
            Account Information
          </h2>

          <div className="space-y-4">
            {/* Avatar */}
            <div className="flex items-center space-x-4 rtl:space-x-reverse pb-4 border-b border-gray-100 dark:border-gray-700">
              <div className="h-16 w-16 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                  {user?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div>
                <p className="text-lg font-medium text-gray-900 dark:text-white">
                  {user?.fullName}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  @{user?.username}
                </p>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <EnvelopeIcon className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Email
                </p>
                <p className="text-sm text-gray-900 dark:text-white">
                  {user?.email || 'No email set'}
                </p>
              </div>
            </div>

            {/* Role */}
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <ShieldCheckIcon className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Role
                </p>
                <p className="text-sm text-gray-900 dark:text-white capitalize">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user?.role === 'admin'
                      ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                      : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                  }`}>
                    {user?.role === 'admin' ? t('users.admin') : t('users.user')}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Change Password Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <KeyIcon className="h-5 w-5 mr-2 text-primary-600" />
            {t('auth.changePassword')}
          </h2>

          <ChangePasswordForm onSuccess={handlePasswordChanged} />
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
